import { AddressLink } from 'Modules'
import { useCallback, useEffect, useState } from 'react'
import type { Address } from 'viem'

import abiPTODistributionV3 from 'abi/abi-pto-distribution-v3.json'
import { PTO_V3_PRICE_DECIMALS } from 'constants/pto-v3'
import { getContract, writeContractAndWait } from 'hooks/contract/use-contract-viem'
import { usePtoOrchestrator } from 'hooks/pto-v3/use-pto-orchestrator'
import { convertPercentContract, convertUnitsContract } from 'utils/contract'
import { logError } from 'utils/logger'

export interface PoolV3Info {
  poolId: number
  poolIds: number[]
  /** price of one whole PTO in USDC (human units) */
  pricePerPTO: number
  /** PTOs the pool may sell (whole PTOs) */
  maxPTOAmount: number
  /** buying fee in percent */
  buyingFee: number
  isEnd: boolean
  totalPTOsPurchase: number
  totalPTODistributed: number
  /** USDC raised, net of fee (human units) */
  totalLoanAmountRaised: number
  /** USDC fee collected (human units) */
  totalFeeCollected: number
}

/**
 * PTO v3 pool read from PTODistribution (the one the orchestrator holds -
 * `orchestrator.distribution()`), latest pool of the PTO via getPoolIds + getContractInfo.
 * null when the PTO has no pool yet.
 */
export const usePoolInfoV3 = (ptoAddress?: AddressLink | null) => {
  const { contract: orchestrator } = usePtoOrchestrator()
  const [poolInfo, setPoolInfo] = useState<PoolV3Info | null>(null)
  const [distributionAddress, setDistributionAddress] = useState<AddressLink | null>(null)
  /** true while the pool is being read from the contract (first load and every refresh) */
  const [loading, setLoading] = useState(false)

  const getPoolInfo = useCallback(async () => {
    if (!orchestrator || !ptoAddress) {
      setPoolInfo(null)
      return
    }
    setLoading(true)
    try {
      const distribution = (await orchestrator.read.distribution()) as Address
      setDistributionAddress(distribution)
      const contract = getContract({ contractAddress: distribution, ABI: abiPTODistributionV3 })

      const poolIdsRes: bigint[] = await contract.read.getPoolIds([ptoAddress])

      const poolIds = (poolIdsRes || []).map((id) => Number(id))
      if (!poolIds.length) {
        setPoolInfo(null)
        return
      }
      const poolId = poolIds[poolIds.length - 1]

      // getContractInfo -> (usdc, PoolParams, phaseIds[], totalPTOsDistributed)
      const [, params, , totalPTOsPurchase] = await contract.read.getContractInfo([BigInt(poolId)])

      const [totalPTODistributed, totalLoanAmountRaised, totalFeeCollected] = await Promise.all([
        contract.read.totalPTODistributed([BigInt(poolId)]),
        contract.read.totalLoanAmountRaised([BigInt(poolId)]),
        contract.read.totalFeeCollected([BigInt(poolId)]),
      ])

      setPoolInfo({
        poolId,
        poolIds,
        pricePerPTO: convertUnitsContract(params.pricePerPTO, PTO_V3_PRICE_DECIMALS),
        maxPTOAmount: Number(params.maxPTOAmount),
        // PTODistribution sprint 31: PoolParams is { ptoAddress, fundingFeeBP, isEnd, pricePerPTO,
        // maxPTOAmount } - fields are read by name, so the new order and the fee rename are safe
        buyingFee: convertPercentContract(params.fundingFeeBP ?? params.buyingFeeBP),
        isEnd: Boolean(params.isEnd),
        totalPTOsPurchase: Number(totalPTOsPurchase),
        totalPTODistributed: Number(totalPTODistributed),
        totalLoanAmountRaised: convertUnitsContract(totalLoanAmountRaised, PTO_V3_PRICE_DECIMALS),
        totalFeeCollected: convertUnitsContract(totalFeeCollected, PTO_V3_PRICE_DECIMALS),
      })
    } catch (error) {
      setPoolInfo(null)
      logError('Read PTO v3 pool info failed', error)
    } finally {
      setLoading(false)
    }
  }, [orchestrator, ptoAddress])

  useEffect(() => {
    getPoolInfo()
  }, [getPoolInfo])

  /**
   * PTODistribution.endPool(poolId) - agent only, permanent. The BE flips the PTO to
   * OFF_SALE from the PoolEnded event; a new pool can be created afterwards.
   */
  const endPool = async (account: Address, poolId: number) => {
    if (!distributionAddress) {
      throw new Error('PTO Distribution address is not available')
    }
    const contract = getContract({
      contractAddress: distributionAddress as Address,
      ABI: abiPTODistributionV3,
    })
    const { request } = await contract.simulate.endPool([BigInt(poolId)], { account })
    const { hash } = await writeContractAndWait(request)
    await getPoolInfo()
    return { txHash: hash }
  }

  return { poolInfo, distributionAddress, loading, getPoolInfo, endPool }
}
